import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpService } from './http.service';
import { ApiResponse } from '../interfaces/api-response';

export interface OnboardingRequest {
  id: number;
  hotel_name: string;
  hotel_city: string;
  hotel_country: string | null;
  hotel_website: string | null;
  contact_name: string;
  contact_email: string;
  contact_phone: string | null;
  contact_position: string | null;
  message: string | null;
  how_did_you_hear: string | null;
  status: OnboardingRequestStatus;
  internal_notes: string | null;
  rejection_reason: string | null;
  processed_by: number | null;
  processed_at: string | null;
  created_hotel_id: number | null;
  country_id: number | null;
  created_at: string;
  updated_at: string;
}

export type OnboardingRequestStatus = 'pending' | 'contacted' | 'in_progress' | 'completed' | 'rejected';

export interface OnboardingRequestStats {
  pending: number;
  contacted: number;
  in_progress: number;
  total: number;
}

export interface OnboardingRequestListData {
  requests: {
    data: OnboardingRequest[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
  stats: OnboardingRequestStats;
}

export interface OnboardingRequestData {
  request: OnboardingRequest;
}

@Injectable({
  providedIn: 'root',
})
export class OnboardingRequestService {
  private readonly basePath = 'v2/extranet/onboarding-requests';

  constructor(private httpService: HttpService) {}

  /**
   * Get paginated list of onboarding requests.
   */
  getRequests(params?: {
    status?: OnboardingRequestStatus;
    page?: number;
    per_page?: number;
  }): Observable<OnboardingRequestListData> {
    const queryParams = new URLSearchParams();

    if (params?.status) {
      queryParams.set('status', params.status);
    }
    if (params?.page) {
      queryParams.set('page', params.page.toString());
    }
    if (params?.per_page) {
      queryParams.set('per_page', params.per_page.toString());
    }

    const queryString = queryParams.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;

    return this.httpService.get<ApiResponse<OnboardingRequestListData>>(url).pipe(
      map((response) => response.data as OnboardingRequestListData)
    );
  }

  /**
   * Get all requests (unpaginated) for client-side aggregation (e.g. referral chart).
   */
  getAllRequests(): Observable<OnboardingRequest[]> {
    return this.httpService
      .get<ApiResponse<OnboardingRequestListData>>(`${this.basePath}?per_page=1000`)
      .pipe(map((response) => (response.data as OnboardingRequestListData).requests.data));
  }

  /**
   * Get a single onboarding request by ID.
   */
  getRequest(id: number): Observable<OnboardingRequestData> {
    return this.httpService.get<ApiResponse<OnboardingRequestData>>(`${this.basePath}/${id}`).pipe(
      map((response) => response.data as OnboardingRequestData)
    );
  }

  /**
   * Update the status of a request.
   */
  updateStatus(
    id: number,
    status: OnboardingRequestStatus,
    internalNotes?: string
  ): Observable<OnboardingRequestData> {
    return this.httpService.patch<ApiResponse<OnboardingRequestData>>(`${this.basePath}/${id}/status`, {
      status,
      internal_notes: internalNotes,
    }).pipe(
      map((response) => response.data as OnboardingRequestData)
    );
  }

  /**
   * Update internal notes only.
   */
  updateNotes(id: number, internalNotes: string): Observable<OnboardingRequestData> {
    return this.httpService.patch<ApiResponse<OnboardingRequestData>>(`${this.basePath}/${id}/notes`, {
      internal_notes: internalNotes,
    }).pipe(
      map((response) => response.data as OnboardingRequestData)
    );
  }

  /**
   * Reject a request with a reason.
   */
  rejectRequest(id: number, rejectionReason: string): Observable<OnboardingRequestData> {
    return this.httpService.post<ApiResponse<OnboardingRequestData>>(`${this.basePath}/${id}/reject`, {
      rejection_reason: rejectionReason,
    }).pipe(
      map((response) => response.data as OnboardingRequestData)
    );
  }

  /**
   * Mark request as completed with the created hotel ID.
   */
  completeRequest(id: number, createdHotelId: number): Observable<OnboardingRequestData> {
    return this.httpService.post<ApiResponse<OnboardingRequestData>>(`${this.basePath}/${id}/complete`, {
      created_hotel_id: createdHotelId,
    }).pipe(
      map((response) => response.data as OnboardingRequestData)
    );
  }
}
